import { useRouteParams } from "@vueuse/router";
import { useCurationExperienceStore } from "@/features/experience-curation/stores/useCurationExperienceStore";
import { useAdditionalServicesMasterdataQuery } from "@/features/masterdata/queries/additional-services-masterdata-query";
import { useExperienceAdditionalServicesQuery } from "@/features/experience-curation/queries/useExperienceAdditionalServicesQuery";
import { usePrimitiveModelWithChanges } from "@/features/core-shared/composables/usePrimitiveModelWithChanges";
import { useComplexModelModelWithChanges } from "@/features/core-shared/composables/useComplexModelWithChanges";
import { areNonCommercialFieldsEditableForUser } from "@/features/experience-curation/lib/viewTypeUtils";
import { filterAndSortAdditionalServices } from "@/features/experience-curation/lib/additional-service-filter-sort";
import type { FunctionalGroupCode } from "@/types/DocumentTypes";

type AdditionalServiceFieldConfig = {
  id: string;
  functionalGroupCode: FunctionalGroupCode;
};

function useAdditionalServiceFieldBase(config: AdditionalServiceFieldConfig) {
  const experienceId = useRouteParams<string>("id");
  const curationExperienceStore = useCurationExperienceStore();

  const additionalServicesQuery = useExperienceAdditionalServicesQuery(experienceId);
  const masterdataQuery = useAdditionalServicesMasterdataQuery();

  const groupCodes = computed(() =>
    (masterdataQuery.data.value || [])
      .filter((s) => s.functional_group_code === config.functionalGroupCode)
      .map((s) => s.code)
  );

  const selectedCode = computed<string>(() => {
    const allCodes = additionalServicesQuery.data.value?.additional_services ?? [];
    return allCodes.find((code) => groupCodes.value.includes(code)) ?? "";
  });

  const filteredServices = computed(() => {
    const allServices = (masterdataQuery.data.value || []).filter(
      (s) => s.functional_group_code === config.functionalGroupCode
    );

    return filterAndSortAdditionalServices(allServices, selectedCode.value);
  });

  const options = computed(() => {
    return filteredServices.value.map((s) => ({ label: s.name, value: s.code }));
  });

  const isReadonly = computed(() => curationExperienceStore.isReadonly || !areNonCommercialFieldsEditableForUser());
  const isVisible = computed(() => !curationExperienceStore.isCommercialView);

  return { additionalServicesQuery, groupCodes, filteredServices, options, isReadonly, isVisible };
}

/**
 * Factory for single-value additional service field stores.
 * The field value is the first matching code from the additional services array.
 */
export function useAdditionalServiceField(config: AdditionalServiceFieldConfig) {
  const { additionalServicesQuery, groupCodes, filteredServices, options, isReadonly, isVisible } =
    useAdditionalServiceFieldBase(config);

  const initialValue = computed<string>(() => {
    const allCodes = additionalServicesQuery.data.value?.additional_services ?? [];
    return allCodes.find((code) => groupCodes.value.includes(code)) ?? "";
  });

  const { model, hasChanges, isFilled, reset } = usePrimitiveModelWithChanges(initialValue);

  return {
    id: config.id,
    model,
    filteredServices,
    options,
    isRequired: false as const,
    isReadonly,
    isVisible,
    hasChanges,
    isFilled,
    reset,
  };
}

/**
 * Factory for multi-value additional service field stores.
 * The field value is all matching codes from the additional services array.
 */
export function useMultiAdditionalServiceField(config: AdditionalServiceFieldConfig) {
  const { additionalServicesQuery, groupCodes, filteredServices, options, isReadonly, isVisible } =
    useAdditionalServiceFieldBase(config);

  const initialValue = computed<string[]>(() => {
    const allCodes = additionalServicesQuery.data.value?.additional_services ?? [];
    return allCodes.filter((code) => groupCodes.value.includes(code));
  });

  const { model, hasChanges, isFilled, reset } = useComplexModelModelWithChanges<string[]>(initialValue);

  return {
    id: config.id,
    model,
    filteredServices,
    options,
    isRequired: false as const,
    isReadonly,
    isVisible,
    hasChanges,
    isFilled,
    reset,
  };
}
